// Builds and parses "pretty" business URLs of the form:
//   /business/<slugified-name>-<businessId>
// The businessId (a UUID) is always the trailing segment, so the route param
// still resolves to the exact same business — only the human-readable prefix
// is new. Links without a name (old shares, notifications, QR codes already
// in the wild) keep working: extractBusinessIdFromParam falls back to the
// raw param when no UUID suffix is found.

const UUID_RE = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** Lowercase, hyphenated, URL-safe version of a business name. */
export function slugifyBusinessName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/**
 * Build the `/business/...` path for a given id + (optional) name.
 * Falls back to the plain-id path when no usable name is available.
 */
export function buildBusinessPath(businessId: string | null | undefined, name?: string | null): string {
  if (!businessId) return "/business";
  const isGenericPlaceholder = !name || name.trim().toLowerCase() === "this table";
  const slug = !isGenericPlaceholder ? slugifyBusinessName(name!) : "";
  return slug ? `/business/${slug}-${businessId}` : `/business/${businessId}`;
}

/**
 * Recover the real businessId from a `business/:id` route param, which may
 * be a plain UUID (old links) or `<slug>-<uuid>` (new pretty links).
 */
export function extractBusinessIdFromParam(param: string | null | undefined): string {
  if (!param) return "";
  const match = param.match(UUID_RE);
  if (match) return match[1];
  if (param.startsWith("this-table-")) {
    return param.slice("this-table-".length);
  }
  return param;
}
